/** MapLibre filter expressions are JSON arrays — we forward without full spec typing. */
type FilterExpression = unknown[]

/** Per-layer filter change to dispatch via `setLayerFilters`. `null` removes the filter. */
export type LayerFilterEntry = {
  layerId: string
  filter: FilterExpression | null
}

interface StyleLayer {
  id: string
  source?: string
  filter?: FilterExpression
}

interface MapStyleShape {
  layers?: StyleLayer[]
}

/** Feature identity + layer id sufficient to build the exclusion expression. */
export interface EditingFeatureFilterTarget {
  layerId: string
  mamId: string | number
}

const buildExclusion = (mamId: string | number): FilterExpression => {
  /**
   * Coerce MVT `mamid` consistently: tiles often stringify ids while click
   * features may expose numbers; `!=` compares strictly and would hide the wrong
   * features (or hide everything) otherwise.
   */
  const mamidProp: FilterExpression = ['to-string', ['get', 'mamid']]
  return ['!=', mamidProp, String(mamId)]
}

const composeFilter = (
  existing: FilterExpression | undefined,
  exclusion: FilterExpression
): FilterExpression =>
  existing && existing.length > 0 ? ['all', existing, exclusion] : exclusion

const findSiblingLayerIdsInternal = (
  style: MapStyleShape,
  primaryLayerId: string
): string[] => {
  const primary = (style.layers ?? []).find(
    styleLayer => styleLayer.id === primaryLayerId
  )
  if (!primary) return []
  if (!primary.source) return [primaryLayerId]
  return (style.layers ?? [])
    .filter(styleLayer => styleLayer.source === primary.source)
    .map(styleLayer => styleLayer.id)
}

export type EditingLayerFilterSnapshot = Record<
  string,
  FilterExpression | undefined
>

/**
 * While a TOC-layer feature is vertex-edited, hide the tiled copy by composing
 * a MapLibre `filter` exclusion on sibling style layers sharing the source.
 *
 * Caller supplies `persistedSnapshots` from edit-lifecycle state. On each call,
 * any layer id not yet keyed is snapshotted from the current style clone;
 * keyed entries keep the baseline filter from edit-start across mapStyle swaps.
 *
 * Caller dispatches `setLayerFilters(filterEntries)` when `shouldDispatch` is
 * true. Reads `mapStyleRaw` without cloning — it only inspects layers to build
 * the per-layer entries. */
export const applyEditingLayerFeatureFilters = ({
  mapStyleRaw,
  editing,
  persistedSnapshots,
}: {
  mapStyleRaw: unknown
  editing: EditingFeatureFilterTarget
  persistedSnapshots: EditingLayerFilterSnapshot
}): {
  nextSnapshots: EditingLayerFilterSnapshot
  filterEntries: LayerFilterEntry[]
  shouldDispatch: boolean
} => {
  const style = mapStyleRaw as MapStyleShape | null
  if (!style?.layers) {
    return {
      nextSnapshots: { ...persistedSnapshots },
      filterEntries: [],
      shouldDispatch: false,
    }
  }

  const siblingIds = findSiblingLayerIdsInternal(style, editing.layerId)
  if (siblingIds.length === 0) {
    return {
      nextSnapshots: { ...persistedSnapshots },
      filterEntries: [],
      shouldDispatch: false,
    }
  }

  const exclusion = buildExclusion(editing.mamId)
  const siblingIdSet = new Set(siblingIds)
  const siblingLayers = style.layers.filter(layer => siblingIdSet.has(layer.id))

  // Snapshot the baseline filter for any sibling not already keyed (first time
  // we see it this edit session); existing keys keep their edit-start baseline.
  const nextSnapshots: EditingLayerFilterSnapshot = {
    ...persistedSnapshots,
    ...Object.fromEntries(
      siblingLayers
        .filter(
          layer =>
            !Object.prototype.hasOwnProperty.call(persistedSnapshots, layer.id)
        )
        .map(layer => [layer.id, layer.filter])
    ),
  }

  const filterEntries: LayerFilterEntry[] = siblingLayers.flatMap(layer => {
    const desired = composeFilter(nextSnapshots[layer.id], exclusion)
    return JSON.stringify(layer.filter) === JSON.stringify(desired)
      ? []
      : [{ layerId: layer.id, filter: desired }]
  })

  return {
    nextSnapshots,
    filterEntries,
    shouldDispatch: filterEntries.length > 0,
  }
}

/** Revert sibling-layer filters captured at edit-start / mid-edit reconciliation. */
export const restoreEditingLayerFeatureFiltersFromSnapshots = ({
  mapStyleRaw,
  snapshots,
}: {
  mapStyleRaw: unknown
  snapshots: EditingLayerFilterSnapshot
}): {
  filterEntries: LayerFilterEntry[]
  shouldDispatch: boolean
} => {
  const ids = Object.keys(snapshots)
  if (ids.length === 0) {
    return { filterEntries: [], shouldDispatch: false }
  }

  const style = mapStyleRaw as MapStyleShape | null
  if (!style?.layers) {
    return { filterEntries: [], shouldDispatch: false }
  }

  const filterEntries: LayerFilterEntry[] = style.layers.flatMap(layer => {
    if (!Object.prototype.hasOwnProperty.call(snapshots, layer.id)) return []
    // `null` filter → setLayerFilters removes the key (restore to "no filter").
    const restored = snapshots[layer.id] ?? null
    return JSON.stringify(layer.filter ?? null) === JSON.stringify(restored)
      ? []
      : [{ layerId: layer.id, filter: restored }]
  })

  return { filterEntries, shouldDispatch: filterEntries.length > 0 }
}
